import { FishingReport, Product, TechniqueFull, WaterbodyDetail } from '@omniafishing/core';
import dayjs from 'dayjs';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDone, LoadingState } from '../../constants/loading_state';
import { useQueryString } from '../../hooks/use_query_string';
import { useReferenceData } from '../../hooks/use_reference_data';
import { useResponsive } from '../../hooks/use_responsive';
import { useUserPreferences } from '../../hooks/use_user_preferences';
import { apiV1 } from '../../lib/api';
import { getSeasonGroupSpeciesFishingReports } from '../../lib/fishing_reports';
import { GlobalQueryParams, OmniaQueryParams } from '../../lib/query_string';
import { scrollToElementTop } from '../../lib/scroll';
import { getMostRecent } from '../../lib/time';
import { ClickKey_LakePage, WebAnalytics } from '../../lib/web_analytics';
import { fetchOmniaVideo, getOmniaVideo, getOmniaVideoItems } from '../../redux/videos';
import { ContentWrapper } from '../content_wrapper/content_wrapper';
import { isLoading } from '../home/home';
import { LoadingCardCarousel } from '../home/loading_card_carousel';
import { OmniaVideoComponent } from '../omnia_video/omnia_video';
import {
  SVGBackgroundClipPathDesktop,
  SVGBackgroundClipPathMobile,
} from './images/background_clip_paths';
import styles from './waterbody_detail.less';
import { baitCategoryNames } from './waterbody_detail_lib/get_top_products';
import { useWaterbodyState } from './waterbody_detail_lib/use_waterbody_state';
import { useWaterbodyTechniques } from './waterbody_detail_lib/use_waterbody_techniques';
import { useFetchWaterbodyTechniqueRecommendations } from './waterbody_detail_lib/use_waterbody_technique_fetch';
import { useWaterbodyWebAnalyticsArgs } from './waterbody_detail_lib/use_waterbody_web_analytics_args';
import { WaterbodyDetailMap } from './waterbody_detail_map';
import { WaterbodyDetailExploreNearby } from './waterbody_detail_sections/waterbody_detail_explore_nearby/waterbody_detail_explore_nearby';
import { WaterbodyDetailFishingReportsOverview } from './waterbody_detail_sections/waterbody_detail_fishing_reports/waterbody_detail_fishing_reports_overview';
import { WaterbodyDetailFollowBanner } from './waterbody_detail_sections/waterbody_detail_follow/waterbody_detail_follow_banner';
import { WaterbodyDetailHeader } from './waterbody_detail_sections/waterbody_detail_header/waterbody_detail_header';
import {
  WaterbodyDetailNav,
  WATERBODY_DETAIL_NAV_HEIGHT_APPROX,
} from './waterbody_detail_sections/waterbody_detail_header/waterbody_detail_nav';
import { WaterbodyDetailInlineKit } from './waterbody_detail_sections/waterbody_detail_kits/waterbody_detail_inline_kit';
import { WaterbodyDetailLearnMore } from './waterbody_detail_sections/waterbody_detail_learn_more/waterbody_detail_learn_more';
import { WaterbodyDetailStats } from './waterbody_detail_sections/waterbody_detail_stats/waterbody_detail_stats';
import { WaterbodyDetailTopProducts } from './waterbody_detail_sections/waterbody_detail_top_products/waterbody_detail_top_products';
import { WaterbodyDetailTopTechniques } from './waterbody_detail_sections/waterbody_detail_top_products_top_techniques/waterbody_detail_top_techniques';
import { WaterbodyDetailSEO } from './waterbody_detail_seo';

export interface WaterbodyDetailProps {
  fishingReports: FishingReport[];
  fishingReportsNearby: FishingReport[];
  mostRecentFishingReportDate: dayjs.Dayjs | null;
  sortedFishingReports: FishingReport[];
  totalFishingReportsCount: number;
  waterbody: WaterbodyDetail;
}
export interface WaterbodyDetailQueryParams {
  email?: string;
  following?: boolean;
  season_group?: string;
  species?: string;
  technique?: string;
}

export enum LakePageIds {
  explore = 'explore',
  fishing_reports = 'fishing_reports',
  learn_more = 'learn_more',
  media = 'media',
  overview = 'overview',
  technique_products = 'technique_products',
  top_techniques = 'top_techniques',
}

export interface TrendingProduct extends Product {
  trending: boolean;
}

export interface FishingReportWithTrendingHotBait extends FishingReport {
  featured_product: TrendingProduct;
}

export const FIRST_PUBLISHED_DATE = '2018-11-01';

export const Waterbody_Detail = (props: WaterbodyDetailProps) => {
  const {
    fishingReports,
    fishingReportsNearby,
    mostRecentFishingReportDate,
    sortedFishingReports,
    totalFishingReportsCount,
    waterbody,
  } = props;

  const { url_slug, id, primary_name, water_temperature, water_temperature_recorded_at } =
    waterbody;

  const dispatch = useDispatch();
  const { allTechniques } = useReferenceData();
  const fishingReportsRef = useRef(null);
  const topTechniquesRef = useRef<HTMLDivElement>(null);
  const overviewRef = useRef();
  const productsRef = useRef();
  const exploreNearbyRef = useRef();
  const learnMoreRef = useRef();
  const mediaRef = useRef();
  const { setUserPreferencesWaterbody } = useUserPreferences();
  const webAnalyticsLakeClickArgs = useWaterbodyWebAnalyticsArgs(waterbody);

  const { selectedSeasonGroup, selectedSpecies, currentSeasonGroup } = useWaterbodyState({
    waterbody,
  });

  const selectedSpeciesName = selectedSpecies.name;
  const selectedSeasonGroupName = selectedSeasonGroup.name;
  const [selectedTechnique, setSelectedTechnique] = useState<TechniqueFull>(null);
  const selectedTechniqueName = selectedTechnique?.name;
  const { getCurrentQuery, idHash, replaceQueryString } = useQueryString();
  const currentQuery = getCurrentQuery<{
    [OmniaQueryParams.technique]: string;
    [GlobalQueryParams.report_id]: string;
    [OmniaQueryParams.species]: string;
    [OmniaQueryParams.season_group]: string;
  }>();
  const initialRenderRef = useRef(false);
  const { isMobile } = useResponsive();
  const WATERBODY_DETAIL_HEADER_HEIGHT_APPROX = isMobile ? 132 : 195;
  const speciesNameQueryParam = currentQuery[OmniaQueryParams.species];
  const seasonGroupQueryParam = currentQuery[OmniaQueryParams.season_group];

  const [hotbaitFishingReports, setHotbaitFishingReports] = useState<
    FishingReportWithTrendingHotBait[]
  >([]);

  const [hotbaitFishingReportsLoadingState, setHotbaitFishingReportsLoadingState] =
    useState<LoadingState>(LoadingState.NOT_STARTED);

  useEffect(() => {
    setHotbaitFishingReportsLoadingState(LoadingState.PENDING);
    apiV1
      .waterbodyHotbaitFishingReportsFetch(url_slug, {
        per_page: 12,
      })
      .then((res) => {
        const { fishing_reports, season_product_family_counts } = res.data.data;
        const hotbaitReports = fishing_reports.map((report) => {
          const seasonId = report.season.id.toString();
          const productCounts = season_product_family_counts[seasonId] || {};
          const productId = report.featured_product?.shopify_product_id;
          const trending = productId && productCounts[productId] > 1;
          return {
            ...report,
            featured_product: {
              ...report.featured_product,
              trending,
            },
          };
        });
        setHotbaitFishingReports(hotbaitReports);
        setHotbaitFishingReportsLoadingState(LoadingState.DONE);
      });
  }, [url_slug, speciesNameQueryParam, seasonGroupQueryParam]);

  useEffect(() => {
    setUserPreferencesWaterbody(waterbody);
  }, [url_slug]);

  /* waterbodyMedia & Omnia_video filtering */
  const omnia_video = useSelector(getOmniaVideo); // TODO: don't use redux for omnia video
  const omnia_video_items = useSelector(getOmniaVideoItems); // TODO: don't use redux for omnia video items
  const [hasRelevantOmniaVideo, setHasRelevantOmniaVideo] = useState<boolean>();

  useEffect(() => {
    apiV1
      .waterbodyLakeBreakdownVideoFetch(url_slug, {
        species: selectedSpeciesName,
        season_group: selectedSeasonGroupName,
      })
      .then((res) => {
        if (res.data.data) {
          // TODO: don't use redux for omnia video
          dispatch(fetchOmniaVideo(res.data.data.slug));
          setHasRelevantOmniaVideo(true);
        } else {
          setHasRelevantOmniaVideo(false);
        }
      });
  }, [selectedSeasonGroupName, selectedSpeciesName]);

  const selectedSeasonSpeciesFishingReports = getSeasonGroupSpeciesFishingReports(
    fishingReports,
    selectedSeasonGroup,
    selectedSpecies
  );

  /* Top Techniques */
  const { topTechniqueName, waterbodyFishingReportTechniquesWithCountsSorted } =
    useWaterbodyTechniques({
      selectedSeasonSpeciesFishingReports,
      url_slug,
      selectedSpeciesName,
      selectedSeasonGroupName,
    });

  // sync state with params if present, if not set state to top waterbody technique (derived above)
  // effect also subscribes to season, species deps to reset top waterbody technique when either are changed
  useEffect(() => {
    const queryParamTechnique = allTechniques.find(
      (technique) => technique.name === currentQuery.technique
    );
    if (queryParamTechnique) {
      setSelectedTechnique(queryParamTechnique);
    } else {
      const topTechnique = allTechniques.find((technique) => technique.name === topTechniqueName);
      setSelectedTechnique(topTechnique);
    }
  }, [currentQuery.technique, selectedSpeciesName, selectedSeasonGroupName, topTechniqueName]);

  const { recommendationsLoadingState, recommendedProductFamilies } =
    useFetchWaterbodyTechniqueRecommendations(
      url_slug,
      selectedSpecies.name,
      selectedTechniqueName,
      selectedSeasonGroup.seasons.map((s) => s.name)
    );
  /* End of Top Techniques */

  const waterbodyDetailProps = {
    waterbody,
    selectedSeasonGroup,
    selectedSpecies,
  };

  // Page Navigation
  const scrollToRef = (currentRef: RefObject<HTMLElement> | void) => {
    if (currentRef && currentRef.current) {
      scrollToElementTop(
        currentRef.current,
        WATERBODY_DETAIL_HEADER_HEIGHT_APPROX + WATERBODY_DETAIL_NAV_HEIGHT_APPROX
      );
    }
  };

  const scrollToTopTechniques = useCallback(() => {
    scrollToRef(topTechniquesRef);
  }, [topTechniquesRef.current, selectedSpeciesName, selectedSeasonGroupName]);

  const scrollToFishingReports = useCallback(() => {
    scrollToRef(fishingReportsRef);
    WebAnalytics.lakePageClick('[overview].(fishing_reports)', webAnalyticsLakeClickArgs);
  }, [
    fishingReportsRef.current,
    webAnalyticsLakeClickArgs,
    selectedSpeciesName,
    selectedSeasonGroupName,
  ]);

  // Scroll to sections on load if hash is present
  useEffect(() => {
    if (idHash === LakePageIds.top_techniques) {
      setTimeout(() => scrollToRef(topTechniquesRef), 0);
    }
  }, [topTechniquesRef.current]);

  useEffect(() => {
    if (idHash === LakePageIds.fishing_reports) {
      setTimeout(() => scrollToRef(fishingReportsRef), 0);
    }
  }, [fishingReportsRef.current]);

  useEffect(() => {
    if (idHash === LakePageIds.overview) {
      setTimeout(() => scrollToRef(overviewRef), 0);
    }
  }, [overviewRef.current]);

  useEffect(() => {
    if (idHash === LakePageIds.technique_products) {
      setTimeout(() => scrollToRef(productsRef), 0);
    }
  }, [productsRef.current]);

  useEffect(() => {
    if (idHash === LakePageIds.media) {
      setTimeout(() => scrollToRef(mediaRef), 0);
    }
  }, [mediaRef.current]);

  const lastUpdatedReportOrTempDate = useMemo(() => {
    const lastUpdatedWaterTempDate = water_temperature_recorded_at
      ? dayjs(water_temperature_recorded_at).startOf('day')
      : null;
    return getMostRecent(mostRecentFishingReportDate, lastUpdatedWaterTempDate);
  }, [mostRecentFishingReportDate, water_temperature_recorded_at]);

  const webAnalyticsLakePageClick = (type: ClickKey_LakePage) => {
    return () => WebAnalytics.lakePageClick(type, webAnalyticsLakeClickArgs);
  };

  useEffect(() => {
    WebAnalytics.fishingIntentChange({
      waterbodyId: id,
      speciesName: selectedSpeciesName,
      seasonGroupName: selectedSeasonGroupName,
    });
  }, [waterbody, selectedSpeciesName, selectedSeasonGroupName]);

  const firstRender = !initialRenderRef.current;
  useEffect(() => {
    if (firstRender && selectedTechniqueName) {
      WebAnalytics.waterbodyPageView({
        season: selectedSeasonGroupName,
        species: selectedSpeciesName,
        technique: selectedTechniqueName,
        waterbody_slug: url_slug,
      });
      initialRenderRef.current = true;
    }
  }, [selectedTechniqueName]);

  // Stats section
  const onTechniqueClick = useCallback(
    (technique: TechniqueFull) => {
      setSelectedTechnique(technique);
      scrollToTopTechniques();
      replaceQueryString({ [OmniaQueryParams.technique]: technique.name });
    },
    [selectedSpeciesName, selectedSeasonGroupName]
  );

  const showVideo = hasRelevantOmniaVideo && omnia_video;
  const followModalProduct = recommendedProductFamilies.find((rf) =>
    baitCategoryNames.includes(rf.product_family.category)
  )?.products[0];

  return (
    <section>
      <WaterbodyDetailSEO
        fishingReports={fishingReports}
        selectedSeasonGroup={selectedSeasonGroup}
        mostRecentFishingReportDate={mostRecentFishingReportDate}
        waterbody={waterbody}
        waterbodyFishingReportTechniquesWithCountsSorted={
          waterbodyFishingReportTechniquesWithCountsSorted
        }
      />
      <section className={styles.page}>
        <section className={styles.lakeContainer}>
          <WaterbodyDetailHeader
            {...waterbodyDetailProps}
            headingTag="h1"
            lastUpdatedDate={lastUpdatedReportOrTempDate}
            productToShowInFollowModal={followModalProduct}
            scrollToFishingReport={scrollToFishingReports}
            showPublishDates
            navElement={
              <WaterbodyDetailNav
                hasCurrentConditions={!!water_temperature}
                hasRelevantOmniaVideo={hasRelevantOmniaVideo}
                hasWaterbodyProducts={hotbaitFishingReports.length > 0}
                urlSlug={url_slug}
                totalWaterbodyFishingReports={totalFishingReportsCount}
              />
            }
          />

          <section id={LakePageIds.overview} ref={overviewRef} className={styles.overview}>
            <div className={styles.fadeWrapper}>
              <ContentWrapper>
                <div className={styles.overviewHeader}>
                  <h2>{primary_name} Overview</h2>
                </div>
                <div className={styles.mapAndStats}>
                  <WaterbodyDetailMap waterbody={waterbody} />
                  <div className={styles.statsWrapper}>
                    <WaterbodyDetailStats
                      {...waterbodyDetailProps}
                      topTechniquesWithCounts={waterbodyFishingReportTechniquesWithCountsSorted}
                      fishingReports={fishingReports}
                      fishingReportsNearby={fishingReportsNearby}
                      onTechniqueClick={onTechniqueClick}
                      onFishingReportsClick={scrollToFishingReports}
                      onSpeciesClick={(s) => {
                        replaceQueryString({
                          [OmniaQueryParams.species]: s.name,
                          [OmniaQueryParams.technique]: null,
                        });
                        scrollToFishingReports();
                      }}
                    />
                  </div>
                </div>
              </ContentWrapper>
            </div>
          </section>
          <section>
            <div className={styles.selectsAndVideoSection}>
              <section id={LakePageIds.technique_products} ref={productsRef}>
                {isLoading(hotbaitFishingReportsLoadingState) ? (
                  <ContentWrapper>
                    <LoadingCardCarousel
                      numberOfCards={6}
                      wrapperClassName={styles.sectionHeader__new}
                      listItemClassName={styles.recentProductReportCard}
                      buttonActions={[`Build A Kit`, 'Add To Cart']}
                      sectionTitle="Recent and Trending Baits"
                      placeholderWidth={200}
                    />
                  </ContentWrapper>
                ) : (
                  <WaterbodyDetailTopProducts
                    hotbaitFishingReports={hotbaitFishingReports}
                    onTechniqueClick={scrollToTopTechniques}
                    waterbody={waterbody}
                  />
                )}
              </section>

              <section>
                <ContentWrapper>
                  {isDone(hotbaitFishingReportsLoadingState) && (
                    <WaterbodyDetailInlineKit
                      key={selectedSpeciesName + currentSeasonGroup.name}
                      recentTrendingReports={hotbaitFishingReports}
                      seasonGroup={selectedSeasonGroup}
                      selectedSeasonSpeciesFishingReports={selectedSeasonSpeciesFishingReports}
                      species={selectedSpecies}
                      techniques={waterbodyFishingReportTechniquesWithCountsSorted}
                      waterbody={waterbody}
                    />
                  )}
                </ContentWrapper>
              </section>

              <section id={LakePageIds.media} className={styles.mediaSection} ref={mediaRef}>
                {showVideo && (
                  <ContentWrapper>
                    <OmniaVideoComponent
                      omnia_video={omnia_video}
                      omnia_video_items={omnia_video_items}
                      className={styles.omniaVideo}
                      publishDatesClassName={styles.omniaVideoDates}
                    />
                  </ContentWrapper>
                )}
              </section>
              <div className={styles.backgroundSVGs}>
                {/* these svg's work with the clip paths .svgWave in the associated less file.
              they are connected using id's */}
                {SVGBackgroundClipPathDesktop}
                {SVGBackgroundClipPathMobile}
              </div>
              <div className={styles.svgWave}></div>
            </div>
            <section ref={fishingReportsRef} id={LakePageIds.fishing_reports}>
              <WaterbodyDetailFishingReportsOverview
                initialFishingReports={sortedFishingReports}
                fishingReportsNearby={fishingReportsNearby}
                productToShowInFollowModal={followModalProduct}
                totalFishingReportsCount={totalFishingReportsCount}
                waterbody={waterbody}
              />
            </section>
            <section>
              <WaterbodyDetailFollowBanner
                waterbody={waterbody}
                className={styles.followBanner}
                hasFishingReports={!!totalFishingReportsCount}
              />
            </section>

            <section
              ref={topTechniquesRef}
              id={LakePageIds.top_techniques}
              className={styles.topTechniques}
            >
              <WaterbodyDetailTopTechniques
                fishingReports={fishingReports}
                recommendationsLoadingState={recommendationsLoadingState}
                recommendedProductFamilies={recommendedProductFamilies}
                selectedTechnique={selectedTechnique}
                topTechniquesOrdered={waterbodyFishingReportTechniquesWithCountsSorted}
                waterbody={waterbody}
              />
            </section>
          </section>
          <ContentWrapper>
            <section id={LakePageIds.explore} ref={exploreNearbyRef}>
              <WaterbodyDetailExploreNearby
                onLakeClick={webAnalyticsLakePageClick('[nearby].(lake)')}
                slug={url_slug}
                waterbody={waterbody}
              />
            </section>
            <section id={LakePageIds.learn_more} ref={learnMoreRef}>
              <WaterbodyDetailLearnMore waterbody={waterbody} />
            </section>
          </ContentWrapper>
        </section>
      </section>

      {/* turn me on if smart banner stops on running on this page */}
      {/* <WaterbodyDetailFollowModal
        waterbody={waterbody}
        open={subscribeModalOpen}
        onModalChange={(open) => setSubscribeModalOpen(open)}
        isTimedPopup={!fishingReportModalIsOpen}
        productToShow={followModalProduct}
        selectedSpeciesName={selectedSpeciesName}
      /> */}
    </section>
  );
};
